import type { QueryAllBlocksRecursivelyResult } from '@dreamstack/accentro-contentful'
import {
  BigTitleNavigation,
  ChildrenBlockSpacingContainer,
  ContentfulBlocks,
} from '@dreamstack/accentro-contentful'
import {
  OtherLocaleUrlsContextProvider,
  useFormat,
  useLocale,
  useTranslation,
} from '@dreamstack/i18n'
import type {
  FullNewsArticleFragment,
  NewsArticleLocaleSlugsFragment,
  OtherNewsArticleFragment,
} from '@dreamstack/investors-graphql'
import { ArticleRichMarkup, DreamstackSeo } from '@dreamstack/seo'
import { AdditionalTrackingInfoContextProvider } from '@dreamstack/tracking'
import map from 'lodash/map'
import Image from 'next/legacy/image'
import type { FunctionComponent } from 'react'
import { useMemo } from 'react'
import 'twin.macro'
import tw, { css, styled, theme } from 'twin.macro'
import {
  Css_HeadingH4,
  FeaturedArticleTemplate,
  GridWithGaps,
  HeadingH1,
  HeadingH2,
  MainLayout,
  NewsArticleContact,
} from '../index'
import { PageTag } from '../layout/PageTag'
import { NewsArticleFile } from './NewsArticleFile'
import { getNewsPathOtherLocales, newsBaseUrls } from './getNewsPath'

const OtherArticlesLayout = tw.div`
  grid 
  col-span-full 
  grid-cols-1 
  md:(grid-cols-2) 
`

const DateWrapper = tw.div`
  border-t-2
  border-accentroGray-full
  mt-px-48 
  ml:(mt-px-64) 
  lg:(mt-px-80)
`

const GridTemplate = css`
  display: grid;
  grid-template:
    'type type'
    'title title'
    'image image'
    'date .'
    'file file'
    'article article'
    'more more' / 1fr 1fr;

  @media (min-width: ${theme`screens.md`}) {
    grid-template:
      'type type type type'
      'title title title title'
      'date image image image'
      'file file file file'
      'article article article article'
      'more more more more' / 1fr 1fr 1fr 1fr;
  }
  @media (min-width: ${theme`screens.ml`}) {
    grid-template:
      'type type type type'
      'title title title title'
      'date image image image'
      '. file file file'
      '. article article article'
      'more more more more' / 1fr 1fr 1fr 1fr;
  }
  /* @media (min-width: ${theme`screens.lg`}) {
    grid-template:
      '. type type type type type type type type type type .'
      '. title title title title title title title title title title .'
      '. date date image image image image image image image image image'
      '. date date article article article article article article article article .'
      '. file file file file file file file file file file .'
      'more more more more more more more more more more more more' / 1fr 1fr 1fr 1fr 1fr 1fr 1fr 1fr 1fr 1fr 1fr 1fr;
  } */
`
const StyledDate = tw.div`block md:(block col-start-1 col-span-2)`

export type NewsArticleProps = {
  article: FullNewsArticleFragment & NewsArticleLocaleSlugsFragment
  otherArticles: OtherNewsArticleFragment[]
  blocks: QueryAllBlocksRecursivelyResult[]
}

const StyledGridWithGaps = styled(GridWithGaps)(() => [GridTemplate])
const MonthAndYear = styled.h4(() => [
  Css_HeadingH4,
  tw`mt-px-8 break-normal w-px-64`,
])

export const NewsArticle: FunctionComponent<
  React.PropsWithChildren<NewsArticleProps>
> = ({ article, otherArticles, blocks }) => {
  const t = useTranslation()
  const formatDate = useFormat()
  const formattedDays = article.date ? formatDate(article.date, 'dd.') : null
  const formattedMonthAndYear = article.date
    ? formatDate(article.date, 'MMMM yyyy')
    : null

  // const translatedNewsTypePlural = t(
  //   `accentro:newsTypes.${article?.newsType}`,
  //   {
  //     count: 2,
  //   }
  // )
  const translatedNewsTypeSingular = t(
    `accentro:newsTypes.${article?.newsType}`
  )

  const locale = useLocale()
  const newsBaseUrl = newsBaseUrls[locale]

  const additionalInfoMemoized = useMemo(
    () => ({
      newsType: article.newsType,
      slug: article.slug,
      articleId: article.sys.id,
      title: article.title,
    }),
    [article.newsType, article.slug, article.sys.id, article.title]
  )

  // const { onHoverStart, controls } = useAnimateIconTranslationRight()
  return (
    <OtherLocaleUrlsContextProvider context={getNewsPathOtherLocales(article)}>
      <AdditionalTrackingInfoContextProvider context={additionalInfoMemoized}>
        <DreamstackSeo
          title={article.title ?? ''}
          image={
            !!article.image?.url && !!article.image?.title
              ? { url: article.image?.url, alt: article.image?.title }
              : undefined
          }
          description={article.previewText ?? ''}
        />
        <ArticleRichMarkup article={article} />
        <MainLayout navbarProps={{ forceDarkTextColor: true }}>
          <StyledGridWithGaps>
            <div tw="mt-px-64 grid-area[type]">
              <PageTag text={translatedNewsTypeSingular}></PageTag>
            </div>
            <div tw="mt-px-32 grid-area[title]">
              <HeadingH1
                tw="hyphens[auto]"
                dangerouslySetInnerHTML={{ __html: article.title ?? '' }}
              />
            </div>
            <div tw="mt-px-32 grid-area[file]">
              <NewsArticleFile file={article.file} />
            </div>
            {!!article.image && !!article.image.url && (
              <div tw="aspect-w-3 aspect-h-2 mt-px-48 ml:(mt-px-64) lg:(mt-px-80) grid-area[image]">
                <Image
                  src={article.image.url}
                  layout={'fill'}
                  objectFit={'cover'}
                  alt={article.image.description || 'Newsarticle image'}
                />
              </div>
            )}
            {article.date && !article.hideDate && (
              <DateWrapper tw="grid-area[date]">
                <StyledDate>
                  <HeadingH2 tw="mt-px-12">{formattedDays}</HeadingH2>
                  <MonthAndYear>{formattedMonthAndYear}</MonthAndYear>
                </StyledDate>
              </DateWrapper>
            )}

            <div tw="grid-area[article] mt-px-48  ml:(mt-px-64)  lg:(mt-px-80)">
              {!!blocks && (
                <ChildrenBlockSpacingContainer>
                  <ContentfulBlocks blocks={blocks} />
                </ChildrenBlockSpacingContainer>
              )}

              <div tw="grid grid-cols-1 mt-px-48 lg:(grid-cols-2 gap-px-64)">
                {map(article.contacts?.items, (contact, i) => {
                  if (!contact) return
                  return (
                    <NewsArticleContact
                      contact={contact}
                      key={contact?.sys?.id || i}
                    />
                  )
                })}
              </div>
            </div>
            <div tw="grid-area[more]">
              <GridWithGaps>
                <OtherArticlesLayout tw="border-t-2" />
              </GridWithGaps>
              {!!otherArticles?.length && (
                <div tw="mt-px-48">
                  <BigTitleNavigation
                    block={{
                      title: t('accentro:newsHub.readMoreTitle'),
                      buttonText: t('accentro:newsHub.allTopics'),
                      staticPageReference: { slug: newsBaseUrl },
                    }}
                  />
                  <GridWithGaps>
                    <OtherArticlesLayout>
                      {map(otherArticles, (article) => {
                        const slug = article.slug
                        return (
                          <FeaturedArticleTemplate
                            key={slug}
                            article={article}
                          />
                        )
                      })}
                    </OtherArticlesLayout>
                  </GridWithGaps>
                </div>
              )}
            </div>
          </StyledGridWithGaps>
        </MainLayout>
      </AdditionalTrackingInfoContextProvider>
    </OtherLocaleUrlsContextProvider>
  )
}
